// Pure, framework-free logic for the accountant Commission Approvals queue.
// One rule per question — UI components must call these, never re-derive.

import { isSuperAdminOwner } from "./brokerageSettings";

export const ACCOUNTING_STATUS = {
  APPROVED: "Approved",
  FLAGGED: "Flagged",
};

const isActive = (u) => !!u && u.type !== 1 && u.type !== 2;

// Pre-launch history stays out of the queue via a FLOOR at the feature's
// launch year — deliberately NOT an exact match against the wall clock, which
// would silently expire every unreviewed December deal on New Year's Day.
// createdat is the ms-timestamp doc id; closingdate is a YYYY-MM-DD input string.
const ACCOUNTING_LAUNCH_YEAR = 2026;

function inAccountingWindow(deal) {
  const created = Number(deal?.createdat || 0);
  if (created && new Date(created).getFullYear() >= ACCOUNTING_LAUNCH_YEAR)
    return true;
  return (
    Number(String(deal?.closingdate || "").slice(0, 4)) >=
    ACCOUNTING_LAUNCH_YEAR
  );
}

// Queue predicate: approved sale turn-ins since launch that accounting has
// not touched yet. Cancelled/Expired deals are skipped — no commission is
// coming — but a deal already Approved/Flagged by accounting keeps its tab.
// (`now` is accepted for API stability but no longer affects the answer.)
export function needsAccountingReview(deal, now = new Date()) {
  if (!deal) return false;
  if (deal.status !== "Approved") return false;
  if (deal.requestType !== "saleTurnIn") return false;
  if (deal.accountingStatus) return false;
  if (deal.dealStage === "Cancelled" || deal.dealStage === "Expired")
    return false;
  return inAccountingWindow(deal);
}

// Which Commission Approvals tab a deal belongs on; null = not shown at all.
export function accountingTab(deal, now = new Date()) {
  if (!deal) return null;
  if (deal.accountingStatus === ACCOUNTING_STATUS.FLAGGED) return "flagged";
  if (deal.accountingStatus === ACCOUNTING_STATUS.APPROVED) return "approved";
  return needsAccountingReview(deal, now) ? "needs-review" : null;
}

// Single source for the accounting write shapes — the queue page and the
// dashboard panel must produce byte-identical patches. Approving always
// clears any fix request; `now` is a ms timestamp.
export function buildAccountingApprovePatch(userId, now) {
  return {
    accountingStatus: ACCOUNTING_STATUS.APPROVED,
    accountingReviewedBy: userId || "",
    accountingReviewedAt: now,
    accountingFlagNote: "",
    accountingFlaggedBy: "",
    accountingFlaggedAt: 0,
  };
}

export function buildAccountingFixPatch(userId, note, now) {
  return {
    accountingStatus: ACCOUNTING_STATUS.FLAGGED,
    accountingFlagNote: note,
    accountingFlaggedBy: userId || "",
    accountingFlaggedAt: now,
  };
}

// Who hears about a flag: the Team Lead or Client Care member who approved
// the deal (or, for auto-approved turn-ins with no approver, whoever
// submitted it) plus the brokerage owner. Deduped; deleted/disabled skipped;
// the flagger never notifies themselves. Returns user objects.
export function flagRecipients(deal, allUsers, flagger) {
  const users = allUsers || [];
  const ids = new Set();
  const approver = users.find((x) => x?.id === deal?.approvedby);
  if (isActive(approver)) {
    ids.add(approver.id);
  } else {
    const submitter = users.find((x) => x?.id === deal?.createdby);
    if (isActive(submitter)) ids.add(submitter.id);
  }
  users
    .filter((x) => isActive(x) && isSuperAdminOwner(x))
    .forEach((x) => ids.add(x.id));
  if (flagger?.id) ids.delete(flagger.id);
  return users.filter((x) => ids.has(x?.id));
}
